import { OnboardPractitionerContext } from '@dfh-corp-sites/onboarding/context'
import { first, firstValueFrom, tap } from 'rxjs'
import { assign, createMachine, InterpreterFrom, StateConfig, StateFrom } from 'xstate'

import {
  persistedPractitionerState$,
  setPractitionerPersistedState,
} from '../hooks/useOnboardPractitionerPersist'

export type OnboardPractitionerEvent =
  | { type: 'CONTINUE' }
  | { type: 'RESTORE' }
  | { type: 'UPDATE_FIELDS'; data: Partial<OnboardPractitionerContext> }

type OnboardPractitionerState = {
  value: 'initial' | 'createAccount' | 'createAccountPersist' | 'invalid' | 'complete'
  context: OnboardPractitionerContext
}

export const machine = createMachine<
  OnboardPractitionerContext & { customerId?: string },
  OnboardPractitionerEvent,
  OnboardPractitionerState
>({
  predictableActionArguments: true,
  id: 'onboard_practitioner',
  initial: 'initial',
  context: {
    firstName: '',
    lastName: '',
    phone: '',
    specialty: '',
    practiceType: '',
    practiceName: '',
    address: {
      street: [],
      city: '',
      state: '',
      zip: '',
      countryCode: '',
    },
  },
  states: {
    initial: {
      on: {
        '*': [
          {
            cond: ({ customerId }) => Boolean(customerId),
            target: 'complete',
          },
          {
            cond: ({ customerId }) => !customerId,
            target: 'createAccount',
          },
        ],
      },
    },
    createAccount: {
      on: {
        CONTINUE: 'createAccountPersist',
      },
    },
    createAccountPersist: {
      entry: 'clearPersistError',
      invoke: {
        src: (context) => (send) => {
          setPractitionerPersistedState({
            state: 'complete',
            context,
          })
          return firstValueFrom(
            persistedPractitionerState$.pipe(
              first((state) => state.loading === false),
              tap((state) => {
                if (state.result?._tag === 'Right') {
                  send({ type: 'CONTINUE' })
                } else {
                  send({ type: 'RESTORE' })
                }
              }),
            ),
          )
        },
        onError: {
          target: 'createAccount',
          actions: 'handlePersistError',
        },
      },
      on: {
        CONTINUE: {
          target: 'complete',
        },
        RESTORE: {
          target: 'createAccount',
          actions: 'handlePersistError',
        },
      },
    },
    invalid: {
      on: {
        RESTORE: 'complete',
      },
    },
    complete: {
      entry: 'submit',
      type: 'final',
    },
  },
  on: {
    UPDATE_FIELDS: {
      actions: assign((context, event) => ({
        ...context,
        ...(event.type === 'UPDATE_FIELDS' ? event.data : {}),
      })),
    },
  },
})

export type OnboardPractitionerMachine = typeof machine

export type OnboardPractitionerInterpreter = InterpreterFrom<OnboardPractitionerMachine>

export type OnboardPractitionerObserverCallback = (state: OnboardPractitionerStateObject) => void

export type OnboardPractitionerStateConfig = StateConfig<
  OnboardPractitionerContext & { customerId?: string },
  OnboardPractitionerEvent
>

export type OnboardPractitionerStateObject = StateFrom<OnboardPractitionerMachine>

export type OnboardPractitionerStateValue = OnboardPractitionerState['value']

export interface OnboardPractitionerClaimParsed {
  state: OnboardPractitionerStateValue
  context: OnboardPractitionerContext
}
